import { Activity } from 'lucide-react';
import type { Signal, TechnicalSnapshot } from '@invest-copilot/shared';
import { isDelistedError, DELISTED_MESSAGE } from '../lib/error-utils';
import { FillExpand } from './FillExpand';

/** 從進場機會「研究台看依據」帶入的 AI 多空文字判讀（researchPlan.decision 子集），整合進技術統計依據卡。 */
export interface AiResearch {
  judgeReasoning?: string;
  bullPoints?: { point: string }[];
  bearPoints?: { point: string }[];
  conflicts?: { signal: string; verdict: string }[];
}

interface Props {
  symbol: string;
  snapshot?: TechnicalSnapshot;
  signal?: Signal;
  liveOverlay?: boolean;
  livePrice?: number;
  loading: boolean;
  error: Error | null;
  /** 併卡模式：去掉外層 .card 外框（由外層 PanelCard 提供框與標題）。 */
  bare?: boolean;
  /** 從進場機會帶入的 AI 多空研判（有值才在 FillExpand 內顯示）。 */
  aiResearch?: AiResearch | null;
}

/**
 * 白話版技術統計卡
 * - 主角常駐：✅ 多方 / ❌ 空方 / 〰️ 中性大方塊 + 總分 badge（使用者平常只看這）。
 * - 逐條因子 + 技術指標原始數字包進 FillExpand：等高右欄留白時自適應填滿（露 peek + 漸層），溢出才「展開全部」。
 */
export function SignalSimpleCard({ symbol: _symbol, snapshot, signal, liveOverlay, livePrice, loading, error, bare = false, aiResearch }: Props) {
  // bare（併卡）模式：flex 鐐讓 FillExpand 能吃右欄 stretch 出來的留白。
  const frameCls = bare ? 'flex-1 flex flex-col min-h-0' : 'card card-accent';

  if (loading) return <div className={bare ? '' : 'card'}><div className="h-20 animate-pulse bg-(--bg-base) rounded" /></div>;
  if (error) return <div className={bare ? '' : 'card'}><div className={isDelistedError(error) ? 'text-(--text-muted) text-sm' : 'text-(--down) text-sm'}>{isDelistedError(error) ? DELISTED_MESSAGE : `無法計算訊號：${error.message}`}</div></div>;
  if (!snapshot || !signal) return null;

  // 把 factors 分類：多方 / 空方 / 中性
  const bull = signal.factors.filter((f) => f.score > 0);
  const bear = signal.factors.filter((f) => f.score < 0);
  const neutral = signal.factors.filter((f) => f.score === 0);

  const levelLabel: Record<Signal['level'], string> = {
    STRONG_BUY: '多方條件集中',
    BUY: '多方條件較多',
    HOLD: '多空接近',
    SELL: '空方條件較多',
    STRONG_SELL: '空方條件集中',
  };
  const levelCls: Record<Signal['level'], string> = {
    STRONG_BUY: 'text-(--up) bg-(--up)/20 border-(--up)',
    BUY: 'text-(--up) bg-(--up)/10 border-(--up)/60',
    HOLD: 'text-(--text-muted) bg-(--bg-base) border-(--border)',
    SELL: 'text-(--down) bg-(--down)/10 border-(--down)/60',
    STRONG_SELL: 'text-(--down) bg-(--down)/20 border-(--down)',
  };

  return (
    <div className={frameCls}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {!bare && <Activity size={16} className="text-(--accent)" />}
          <div>
            {!bare && (
              <div className="font-bold text-(--text-primary) text-sm flex items-center gap-1.5">
                技術統計依據
                {liveOverlay && (
                  <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border border-(--up)/50 bg-(--up)/10 text-(--up)">
                    ⚡ 即時
                  </span>
                )}
              </div>
            )}
            <div className="text-[10px] text-(--text-muted)">
              {snapshot.regime === 'trending' ? '📈 趨勢盤' : snapshot.regime === 'volatile' ? '⚡ 高波動' : '↔ 盤整'}
              {snapshot.atrPct != null && ` · 波動 ${(snapshot.atrPct * 100).toFixed(1)}%`}
              {liveOverlay && livePrice != null && ` · ${livePrice.toFixed(2)}`}
              {bare && liveOverlay && <span className="ml-1 text-(--up) font-bold">⚡ 即時</span>}
            </div>
          </div>
        </div>
        <span className={`px-2.5 py-1 rounded-md border-2 font-bold text-xs tnum ${levelCls[signal.level]}`}>
          {levelLabel[signal.level]} ({signal.score >= 0 ? '+' : ''}{signal.score})
        </span>
      </div>

      {/* 多 / 空 / 中性 因子數量條（主角常駐：label 與數字同列） */}
      <div className="grid grid-cols-3 gap-1.5">
        <CountTile label="✅ 多方" count={bull.length} cls="text-(--up) border-(--up)/50 bg-(--up)/10" />
        <CountTile label="❌ 空方" count={bear.length} cls="text-(--down) border-(--down)/50 bg-(--down)/10" />
        <CountTile label="〰️ 中性" count={neutral.length} cls="text-(--text-muted) border-(--border)" />
      </div>

      {/* 因子 + 原始數字：FillExpand 填補右欄等高留白，溢出才「展開全部」（平常只看上面大方塊）。
          --fe-peek 設 0：不用固定 peek 灌大卡片（那會讓右欄反超左欄 K 線、害左欄空白），
          改靠 flex-grow 只填補「左欄 K 線(固定高)撐出的 slack」，所以左欄永遠是等高基準、不留白。 */}
      <div className="mt-2 flex-1 flex flex-col min-h-0" style={{ '--fe-peek': '0px' } as React.CSSProperties}>
        <FillExpand moreLabel={aiResearch ? `🧠 展開 AI 多空研判 + ${signal.factors.length} 因子明細` : `展開全部（${signal.factors.length} 因子 + 指標）`} lessLabel="收合明細">
          {aiResearch && <AiResearchBlock ai={aiResearch} />}
          <div className="space-y-1">
            {bull.map((f) => <FactorRow key={f.name} icon="✅" name={f.name} reason={f.reason} score={f.score} accent="up" />)}
            {bear.map((f) => <FactorRow key={f.name} icon="❌" name={f.name} reason={f.reason} score={f.score} accent="down" />)}
            {neutral.map((f) => <FactorRow key={f.name} icon="〰️" name={f.name} reason={f.reason} score={f.score} accent="flat" />)}
          </div>

          {/* 技術指標原始數字 */}
          <div className="mt-2 pt-2 border-t border-(--border)">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-(--text-muted)">技術指標原始數字</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 text-[11px] tnum">
              {snapshot.ma20 != null && <Stat label="MA20" value={snapshot.ma20.toFixed(2)} />}
              {snapshot.ma60 != null && <Stat label="MA60" value={snapshot.ma60.toFixed(2)} />}
              {snapshot.rsi14 != null && <Stat label="RSI14" value={snapshot.rsi14.toFixed(1)} hint={snapshot.rsi14 >= 70 ? '超買' : snapshot.rsi14 <= 30 ? '超賣' : ''} />}
              {snapshot.macd && <Stat label="MACD-H" value={snapshot.macd.hist.toFixed(3)} hint={snapshot.macd.hist > 0 ? '多' : '空'} />}
              {snapshot.bollinger && <Stat label="BB %B" value={snapshot.bollinger.percentB.toFixed(2)} hint={snapshot.bollinger.percentB > 1 ? '突破上' : snapshot.bollinger.percentB < 0 ? '跌破下' : ''} />}
              {snapshot.stochastic && <Stat label="Stoch K" value={snapshot.stochastic.k.toFixed(1)} hint={snapshot.stochastic.k >= 80 ? '超買' : snapshot.stochastic.k <= 20 ? '超賣' : ''} />}
              {snapshot.volRatio != null && <Stat label="量比" value={snapshot.volRatio.toFixed(2)} hint={snapshot.volRatio > 1.5 ? '放大' : snapshot.volRatio < 0.7 ? '萎縮' : ''} />}
              {snapshot.obvDivergence && snapshot.obvDivergence !== 'none' && (
                <Stat label="OBV 背離" value={snapshot.obvDivergence === 'bullish' ? '多' : '空'} />
              )}
            </div>
          </div>
        </FillExpand>
      </div>
    </div>
  );
}

function AiResearchBlock({ ai }: { ai: AiResearch }) {
  const hasBull = !!ai.bullPoints && ai.bullPoints.length > 0;
  const hasBear = !!ai.bearPoints && ai.bearPoints.length > 0;
  const hasConflict = !!ai.conflicts && ai.conflicts.length > 0;
  if (!ai.judgeReasoning && !hasBull && !hasBear && !hasConflict) return null;
  return (
    <div className="mb-2 rounded border border-(--accent)/30 bg-(--accent)/5 p-2">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-(--accent)">🧠 AI 多空研判</div>
      {ai.judgeReasoning && <div className="mb-1.5 text-[11px] leading-snug text-(--text-secondary)">{ai.judgeReasoning}</div>}
      <div className="grid gap-2 sm:grid-cols-2">
        {hasBull && (
          <div>
            <div className="mb-0.5 text-[10px] font-bold text-(--up)">📈 多方依據</div>
            <ul className="space-y-0.5 text-[11px] text-(--text-secondary)">{(ai.bullPoints ?? []).slice(0, 4).map((p, i) => <li key={i}>• {p.point}</li>)}</ul>
          </div>
        )}
        {hasBear && (
          <div>
            <div className="mb-0.5 text-[10px] font-bold text-(--down)">📉 空方依據</div>
            <ul className="space-y-0.5 text-[11px] text-(--text-secondary)">{(ai.bearPoints ?? []).slice(0, 4).map((p, i) => <li key={i}>• {p.point}</li>)}</ul>
          </div>
        )}
      </div>
      {hasConflict && (
        <div className="mt-1.5">
          <div className="mb-0.5 text-[10px] font-bold text-(--text-muted)">⚖ 衝突判斷</div>
          <ul className="space-y-0.5 text-[11px] text-(--text-secondary)">{(ai.conflicts ?? []).map((c, i) => <li key={i}>• <b>{c.signal}</b>：{c.verdict}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function CountTile({ label, count, cls }: { label: string; count: number; cls: string }) {
  return (
    <div className={`px-2 py-1.5 rounded border ${cls} flex items-center justify-between gap-1`}>
      <span className="text-[10px] opacity-80">{label}</span>
      <span className="text-lg font-bold tnum leading-none">{count}</span>
    </div>
  );
}

function FactorRow({ icon, name, reason, score, accent }: {
  icon: string; name: string; reason: string; score: number; accent: 'up' | 'down' | 'flat';
}) {
  const cls = accent === 'up' ? 'text-(--up)' : accent === 'down' ? 'text-(--down)' : 'text-(--text-muted)';
  return (
    <div className="flex items-start gap-2 text-[11px] py-0.5">
      <span className="shrink-0 mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <span className={`font-semibold ${cls}`}>{name}</span>
        <span className="text-(--text-muted) ml-1">— {reason}</span>
      </div>
      <span className={`shrink-0 tnum font-bold ${cls}`}>
        {score > 0 ? `+${score}` : score}
      </span>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="px-1.5 py-0.5 rounded bg-(--bg-base) border border-(--border)">
      <div className="text-[9px] text-(--text-muted)">{label}</div>
      <div className="font-semibold text-(--text-primary)">
        {value}
        {hint && <span className="ml-1 text-[9px] text-(--accent)">({hint})</span>}
      </div>
    </div>
  );
}
